import React, { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Users, CheckCircle2, ChevronLeft, ChevronRight, Map, Images, Star,
} from "lucide-react";
import LogoutButton from "./Logout";
import { supabase } from "../../supabaseClient";
import { AdminNotificationsContext } from "./AdminNotificationsContext";
import "./admin-layout.css";

const NAV_ITEMS = [
  { to: "/admin",          icon: LayoutDashboard, label: "Dashboard",  end: true },
  { to: "/admin/vendors",  icon: Users,            label: "Vendors"             },
  { to: "/admin/accepted", icon: CheckCircle2,     label: "Accepted"            },
  { to: "/admin/Layout",   icon: Map,              label: "Layout"              },
  { to: "/admin/media",    icon: Images,           label: "Media"               },
  { to: "/admin/signup-requests", icon: Star, label: "Requests" },
];

const LAST_SEEN_KEY = "admin_signup_requests_last_seen";

// Anything created after this counts as "new since last time the tab was
// opened". Defaults to epoch so a first-ever admin session shows the true
// backlog rather than 0.
const getLastSeen = () => localStorage.getItem(LAST_SEEN_KEY) || new Date(0).toISOString();

const AdminLayout = () => {
  const [collapsed, setCollapsed] = useState(true);
  const [newSignupCount, setNewSignupCount] = useState(0);
  const lastSeenRef = useRef(getLastSeen());

  const fetchCount = useCallback(async () => {
    const { count, error } = await supabase
      .from("vendor_signup_requests")
      .select("id", { count: "exact", head: true })
      .gt("created_at", lastSeenRef.current);

    if (!error) setNewSignupCount(count || 0);
  }, []);

  useEffect(() => {
    fetchCount();

    // Live bump while an admin is already sitting on the dashboard.
    // Requires realtime/replication enabled on vendor_signup_requests in
    // the Supabase dashboard (Database → Replication) — harmless no-op if
    // it isn't, the initial fetchCount() above still works either way.
    const channel = supabase
      .channel("admin-signup-requests-badge")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "vendor_signup_requests" },
        () => setNewSignupCount((c) => c + 1)
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [fetchCount]);

  // Called by the Requests tab every time it's opened — resets the
  // baseline to "now" so the badge clears and only future signups count.
  const markSignupRequestsSeen = useCallback(() => {
    const now = new Date().toISOString();
    localStorage.setItem(LAST_SEEN_KEY, now);
    lastSeenRef.current = now;
    setNewSignupCount(0);
  }, []);

  return (
    <AdminNotificationsContext.Provider value={{ newSignupCount, markSignupRequestsSeen }}>
      <div className={`admin-layout ${collapsed ? "collapsed" : ""}`}>
        <aside className="admin-sidebar">
          <div className="admin-header">
            <div className="admin-brand">
              <div className="admin-brand-dot" />
              {!collapsed && <span className="admin-brand-name">BizBaz</span>}
            </div>
            <button
              className="toggle-btn"
              onClick={() => setCollapsed(!collapsed)}
              title={collapsed ? "Expand" : "Collapse"}
            >
              {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          </div>

          <nav className="admin-nav">
            {NAV_ITEMS.map(({ to, icon: Icon, label, end }) => {
              const showBadge = to === "/admin/signup-requests" && newSignupCount > 0;
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) => `admin-link ${isActive ? "admin-link--active" : ""}`}
                >
                  <span className="admin-link-icon">
                    <Icon size={18} />
                    {showBadge && (
                      <span className="admin-notif-badge">
                        {newSignupCount > 9 ? "9+" : newSignupCount}
                      </span>
                    )}
                  </span>
                  {!collapsed && <span className="admin-link-label">{label}</span>}
                  {collapsed && <span className="admin-tooltip">{label}</span>}
                </NavLink>
              );
            })}
          </nav>

          <div className="admin-sidebar-footer">
            <LogoutButton collapsed={collapsed} />
          </div>
        </aside>

        <main className="admin-content">
          <Outlet />
        </main>
      </div>
    </AdminNotificationsContext.Provider>
  );
};

export default AdminLayout;